/**
 * WATCH zone — "What's going on" (the report rack).
 *
 * Single horizontal scrollable row of compact summary tiles, rendered BELOW
 * the ACT hero (2026-06-10 information-budget redesign — primary signal
 * first). Cognitive contract: ACT = "what to do", WATCH = "what's going on
 * in my org" — observations AND redone platform reports. Each tile = one
 * content domain, plain-language lead stat, click-in to its native detail
 * page.
 *
 * Provision seam: future report tiles (Site Variance, Energy & Water Use,
 * Utility Watch) arrive as new entries in `watchTileRegistry` + a renderer —
 * zero homepage refactor. Only domains with live data render; no
 * "coming-soon" placeholders.
 *
 * Scale valve (C-valve, not built): past ~4-5 tiles the row gains a collapse
 * affordance ("What's going on · N reports").
 *
 * Standards: the period-over-period delta uses the shared `<PercentChange>`;
 * text uses theme variants. The cerulean inset stripe is the zone signature;
 * per-tile CTA only.
 */
import type React from "react"

import { PercentChange } from "@/components/percent-change"
import { Button } from "@/nzds/inputs/button"
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts"

import { ArrowForwardRounded, ArrowUpwardRounded } from "@mui/icons-material"
import { Box, Chip, Divider, Stack, Typography } from "@mui/material"
import type { Theme } from "@mui/material/styles"
import { styled, useTheme } from "@mui/material/styles"

import { useInsightsNavigate } from "../data/use-insights-navigate"
import { Eyebrow, SectionTitle, cerulean } from "./atoms"
import type { WatchTile, WatchTileType } from "./insights-homepage.types"

// ── tile chrome ──────────────────────────────────────────────────────────────

// Platform card chrome (Paper elevation-2, theme radius, borderless) + the
// one deliberate accent: the cerulean inset stripe, the WATCH-zone signature.
const TileCard = styled(Box, {
  shouldForwardProp: (p) => p !== "minWidth",
})<{ minWidth?: number }>(({ theme, minWidth }) => ({
  position: "relative",
  flex: "0 0 auto",
  minWidth: minWidth ?? 300,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  gap: theme.spacing(1.5),
  padding: "16px 18px",
  minHeight: 184,
  borderRadius: theme.typography.pxToRem(8),
  backgroundColor: theme.palette.common.white,
  boxShadow: theme.shadows[2],
  borderLeft: `4px solid ${cerulean(theme).main}`,
}))

const TileHeader: React.FC<{ icon?: React.ReactNode; title: string }> = ({
  icon,
  title,
}) => {
  const theme = useTheme()
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      {icon ? (
        <Box sx={{ display: "inline-flex", color: cerulean(theme).main }}>
          {icon}
        </Box>
      ) : null}
      <Eyebrow sx={{ color: cerulean(theme).dark }}>{title}</Eyebrow>
    </Stack>
  )
}

// ── load-profile tile (portfolio end-use donut) ──────────────────────────────

/** Canonical PRISM end-use → spectrum color. Grey for unknown keys. */
const endUseColor = (theme: Theme, key: string): string => {
  const s = theme.palette.spectrum
  const map: Record<string, string> = {
    cooling: s.blue[400],
    heating: s.red[400],
    fans: s.cerulean[400],
    pumps: s.cerulean[200],
    lighting: s.mustard[400],
    plugs: s.purple[400],
    water_heating: s.peach[400],
    refrigeration: s.green[400],
  }
  return map[key] ?? theme.palette.grey[300]
}

const LoadProfileTile: React.FC<{ tile: WatchTile }> = ({ tile }) => {
  const theme = useTheme()
  // Discriminate the WatchTile union — the registry guarantees the type, this
  // narrows `tile.payload` for the type checker.
  if (tile.type !== "load-profile") {
    return null
  }
  const payload = tile.payload
  const data = payload.segments.map((seg) => ({
    name: seg.label,
    value: seg.kwh,
    key: seg.key,
  }))

  return (
    <TileCard minWidth={320}>
      <TileHeader title={tile.title} />
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ flex: 1, alignItems: "center" }}
      >
        <Box
          sx={{ position: "relative", width: 96, height: 96, flex: "0 0 auto" }}
        >
          <ResponsiveContainer height="100%" width="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                endAngle={-270}
                innerRadius={32}
                isAnimationActive={false}
                nameKey="name"
                outerRadius={46}
                startAngle={90}
                stroke="none"
              >
                {data.map((d) => (
                  <Cell key={d.key} fill={endUseColor(theme, d.key)} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <Typography sx={{ fontWeight: "bold", lineHeight: 1 }} variant="h2">
              {payload.total_value}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            columnGap: 1,
            rowGap: 0.25,
          }}
        >
          {payload.segments.map((seg) => (
            <Stack
              key={seg.key}
              direction="row"
              spacing={0.5}
              sx={{ alignItems: "center" }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: 0.5,
                  bgcolor: endUseColor(theme, seg.key),
                  flex: "0 0 auto",
                }}
              />
              <Typography color="text.secondary" noWrap variant="dataLabel">
                {seg.label}
              </Typography>
            </Stack>
          ))}
        </Box>
      </Stack>
      <Stack
        direction="row"
        sx={{ alignItems: "center", justifyContent: "space-between" }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Typography color="text.secondary" variant="caption">
            {payload.total_unit}
          </Typography>
          {payload.delta_pct !== undefined ? (
            <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
              <PercentChange value={payload.delta_pct} />
              {payload.delta_label ? (
                <Typography color="text.secondary" variant="dataLabel">
                  {payload.delta_label}
                </Typography>
              ) : null}
            </Stack>
          ) : null}
        </Stack>
        {tile.cta ? (
          <Button
            endIcon={<ArrowForwardRounded />}
            href={tile.cta.href}
            size="small"
            variant="text"
          >
            {tile.cta.label}
          </Button>
        ) : null}
      </Stack>
    </TileCard>
  )
}

// ── site-variance tile (cross-site outlier report) ───────────────────────────

/** Status → emphasis dot color. Unknown/absent status reads as neutral. */
const statusColor = (theme: Theme, status: string | null): string => {
  if (status === "attention") {
    return theme.palette.warning.main
  }
  if (status === "ok" || status === "good") {
    return theme.palette.success.main
  }
  return theme.palette.grey[400]
}

const SiteVarianceTile: React.FC<{ tile: WatchTile }> = ({ tile }) => {
  const theme = useTheme()
  const navigate = useInsightsNavigate()
  // Discriminate the WatchTile union (see LoadProfileTile).
  if (tile.type !== "site-variance") {
    return null
  }
  // Generic module-agnostic SummaryTile (GET /neat/insights) — read its
  // fields as-is, never reconstruct a bespoke shape from them, so future
  // WATCH modules reuse this same tile contract.
  const p = tile.payload
  const varianceCta = tile.cta

  return (
    <TileCard minWidth={300}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", justifyContent: "space-between" }}
      >
        <TileHeader title={tile.title} />
        {p.period_label ? (
          <Chip
            label={p.period_label}
            size="small"
            sx={{ letterSpacing: "0.04em", fontWeight: "bold" }}
            variant="outlined"
          />
        ) : null}
      </Stack>

      <Stack spacing={1.25} sx={{ flex: 1, justifyContent: "center" }}>
        {/* Hero — the module's own composed headline sentence. */}
        <Typography sx={{ fontWeight: "bold", lineHeight: 1.2 }} variant="h1">
          {p.headline}
        </Typography>

        {p.highlight ? (
          <>
            <Divider />

            {/* Worst offender — a muted eyebrow labels the teaser, the site
                reads as the line's subject, and the deviation is the figure.
                Demoted to h2 so the tile headline (h1) stays the lead; the
                percentile is a plain caption, not another chip. */}
            <Stack spacing={0.5}>
              <Eyebrow>Worst offender</Eyebrow>
              <Typography sx={{ fontWeight: "bold" }} variant="body2">
                {p.highlight.label}
              </Typography>
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ alignItems: "center" }}
              >
                {/* Direction glyph only — neutral ink (historical framing, no
                    green/red). */}
                <ArrowUpwardRounded
                  sx={{ fontSize: 18, color: "text.secondary" }}
                />
                <Typography
                  sx={{ fontWeight: "bold", lineHeight: 1.1 }}
                  variant="h2"
                >
                  {p.highlight.value}
                </Typography>
              </Stack>
              {p.highlight.sub ? (
                <Typography color="text.secondary" variant="dataLabel">
                  {p.highlight.sub}
                </Typography>
              ) : null}
            </Stack>
          </>
        ) : null}
      </Stack>

      <Stack
        direction="row"
        sx={{ alignItems: "center", justifyContent: "space-between" }}
      >
        <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: statusColor(theme, p.status),
              flex: "0 0 auto",
            }}
          />
          {p.status ? (
            <Typography color="text.secondary" variant="dataLabel">
              {p.status}
            </Typography>
          ) : null}
        </Stack>
        {varianceCta ? (
          <Button
            endIcon={<ArrowForwardRounded />}
            // react-router relative nav (nzds Button `href` renders a plain
            // anchor → browser-relative, which drops the /neat segment).
            onClick={() => {
              navigate(varianceCta.href)
            }}
            size="small"
            variant="text"
          >
            {varianceCta.label}
          </Button>
        ) : null}
      </Stack>
    </TileCard>
  )
}

// ── registry + zone ──────────────────────────────────────────────────────────

// The report rack's provision seam: a redone platform report (Site Variance,
// Energy & Water Use, Utility Watch, …) ships as one new WatchTileType + one
// renderer registered here. WatchZone never switches on `type`.
export const watchTileRegistry: Record<
  WatchTileType,
  React.FC<{ tile: WatchTile }>
> = {
  "load-profile": LoadProfileTile,
  "site-variance": SiteVarianceTile,
}

const ScrollRow = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  overflowX: "auto",
  paddingBottom: theme.spacing(1),
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": { height: 6 },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.divider,
    borderRadius: 3,
  },
}))

export const WatchZone: React.FC<{ tiles: readonly WatchTile[] }> = ({
  tiles,
}) => {
  if (tiles.length === 0) {
    return null
  }
  return (
    <Box aria-label="What's going on" component="section">
      <Box sx={{ mb: 1.5 }}>
        <SectionTitle>What&apos;s going on</SectionTitle>
      </Box>
      <ScrollRow>
        {tiles.map((tile) => {
          const Renderer = watchTileRegistry[tile.type]
          return <Renderer key={`${tile.type}-${tile.title}`} tile={tile} />
        })}
      </ScrollRow>
    </Box>
  )
}
